import { z } from 'zod';

/**
 * Catálogo da revisão de conversas (spec voll-specs `conversation-reviews` §6.2).
 * Ids dos agentes = domínios registrados no agents + `reception`. Um id novo no agents
 * sem entrada aqui aparece como `catalogDrift` no run do analytics.
 */
export const REVIEW_AGENT_IDS = [
  'flight',
  'hotel',
  'bus',
  'rental',
  'mobility',
  'trip',
  'flight_schedule_change',
  'approvals',
  'policy',
  'reception',
] as const;
export const ReviewAgentSchema = z.enum(REVIEW_AGENT_IDS);
export type ReviewAgent = z.infer<typeof ReviewAgentSchema>;

export const ReviewExpectedAgentSchema = z.enum([
  'flight',
  'hotel',
  'bus',
  'rental',
  'mobility',
  'trip',
  'flight_schedule_change',
  'approvals',
  'policy',
  'reception',
  'none',
]);
export type ReviewExpectedAgent = z.infer<typeof ReviewExpectedAgentSchema>;

export const REVIEW_NEEDS = [
  'flight.search_book',
  'flight.change',
  'flight.question',
  'hotel.search_book',
  'hotel.question',
  'bus.search_book',
  'rental.search_book',
  'mobility.search_book',
  'trip.consult',
  'trip.status',
  'trip.voucher',
  'trip.baggage',
  'trip.checkin',
  'trip.cancel_rules',
  'trip.change',
  'approvals.decide',
  'cancel',
  'refund',
  'expense',
  'invoice',
  'policy_question',
  'greeting_or_test',
  'other',
] as const;
export const ReviewNeedSchema = z.enum(REVIEW_NEEDS);
export type ReviewNeed = z.infer<typeof ReviewNeedSchema>;

/** `abandoned`: o assistente fez a parte dele (pergunta necessária e correta, ofertas, checkout ou
 *  próximo passo claro) e o cliente não seguiu — não é falha do agente. */
export const ReviewRequestOutcomeSchema = z.enum([
  'resolved',
  'partial',
  'unresolved',
  'abandoned',
]);
export type ReviewRequestOutcome = z.infer<typeof ReviewRequestOutcomeSchema>;

export const ReviewConversationOutcomeSchema = z.enum([
  'resolved',
  'partial',
  'unresolved',
  'abandoned',
  'no_request',
]);
export type ReviewConversationOutcome = z.infer<typeof ReviewConversationOutcomeSchema>;

/** `not_served`: ninguém respondeu no trecho do pedido (nenhum agente nem operador).
 *  `handled_by_operator`: no trecho do pedido só o operador respondeu (nenhum agente).
 *  `out_of_scope_by_design`: só em revisões antigas (cancelamento antes de 09/10/2026, hoje do
 *  trip com transferência a humano); a composição não o produz mais. */
export const ReviewRoutingSchema = z.enum([
  'correct',
  'wrong_agent',
  'not_served',
  'no_agent',
  'out_of_scope_by_design',
  'handled_by_operator',
]);
export type ReviewRouting = z.infer<typeof ReviewRoutingSchema>;

/** Os motivos do 👎 (`MessageFeedbackReasonSchema`) + `no_reply` + `error`. */
export const ReviewReasonSchema = z.enum([
  'wrong_info',
  'incomplete',
  'misunderstood',
  'bad_options',
  'too_slow',
  'other',
  'no_reply',
  'error',
]);
export type ReviewReason = z.infer<typeof ReviewReasonSchema>;

/** `operation`: o cliente ficou sem resposta com a conversa nas mãos do operador. */
export const ReviewFixTargetSchema = z.enum([
  'platform',
  'operation',
  'routing',
  'new_agent',
  'none',
  'improve_agent:flight',
  'improve_agent:hotel',
  'improve_agent:bus',
  'improve_agent:rental',
  'improve_agent:mobility',
  'improve_agent:trip',
  'improve_agent:flight_schedule_change',
  'improve_agent:approvals',
  'improve_agent:policy',
  'improve_agent:reception',
]);
export type ReviewFixTarget = z.infer<typeof ReviewFixTargetSchema>;

export function improveAgentTarget(agent: ReviewAgent): ReviewFixTarget {
  return ReviewFixTargetSchema.parse(`improve_agent:${agent}`);
}

// ── Dimensões dos indicadores de revisão ─────────────────────────────────
export const ReviewOutcomeDimSchema = z.enum([
  'resolved',
  'partial',
  'unresolved',
  'abandoned',
  'no_request',
  'limbo',
  'error',
  'not_judged',
]);
export type ReviewOutcomeDim = z.infer<typeof ReviewOutcomeDimSchema>;

export const JudgeArmSchema = z.enum(['llm', 'jev']);
export type JudgeArm = z.infer<typeof JudgeArmSchema>;

export const FeedbackPolaritySchema = z.enum(['negative', 'positive']);
export type FeedbackPolarity = z.infer<typeof FeedbackPolaritySchema>;
